import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Choice,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  Tabs,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesos, parsePesos } from '@dayon/money';
import {
  REDEMPTION_REASON_SEPARATOR,
  POINTS_REVERSAL_REASON,
  AUDIT_REASON_MIN_LENGTH,
} from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { sentence } from '../profile/vocabulary.ts';
import { AddRewardDialog } from './AddRewardDialog.tsx';
import { AdjustmentQueue } from './AdjustmentQueue.tsx';
import { PointsAdjustmentDialog } from './PointsAdjustmentDialog.tsx';

/**
 * §13 Points Mall — the loyalty-currency console.
 *
 * THE DATA SUPPLY, in full:
 *   GET  /api/points/redemptions  the approval queue, pending first, LIMIT 100
 *   GET  /api/points/rewards      the catalogue
 *
 * THE WRITES, in full:
 *   POST /api/points/redemptions/:id/approve  points.verdict — stamps the
 *        checker and takes one unit off a MERCH reward's stock
 *   POST /api/points/redemptions/:id/reject   points.verdict — records the
 *        reason and RE-CREDITS the points through points.points_ledger
 *
 * The lifecycle this screen used to describe as unimplemented policy is now
 * behaviour. A redemption debits the player when it is raised — the request
 * path posts a negative REDEMPTION row — approving takes the merch unit and
 * moves no points, and refusing returns exactly what was taken under
 * REDEMPTION_REVERSAL with `source_ref` naming the redemption.
 *
 * THE EARNING RATE, added since — §13's other half:
 *   GET  /api/points/earning-rules      platform.view
 *   POST /api/points/earning-rules/:id  platform.manage
 *
 * This tab used to say "Earning rules are not stored anywhere yet", and it was
 * right: the rate lived as the decimal string "0.01" in `platform.setting` and
 * NOTHING READ IT. `points.points_ledger` had two writers, a redemption and its
 * reversal, so a balance could only be granted by hand and then spent — a shop
 * with no wages. `sql/460_points_earning.sql` gives the rate a table with
 * integer arithmetic and a brand, and the `points.award` job settles closed
 * gaming days against it. What this tab shows is that rule and what it has
 * actually paid, both read from rows.
 *
 * What is still missing is named as a gap rather than filled in: the design's
 * per-tier multiplier and 500k-points VIP monthly cap have no table, no
 * per-player counter and no endpoint, and the ₱8.74M liability tile has no
 * source at all. A plausible "₱8.74M outstanding" is the kind of figure a
 * finance lead puts in a board pack.
 */

/* ── Wire shapes ────────────────────────────────────────────────────────────
   Straight off the SQL SELECT list: snake_case, and every integer text-cast so
   a BIGINT never lands here as a lossy JS number. */

interface RewardRow {
  id: string;
  name: string;
  /** CASH · BONUS · MERCH — three kinds, not the design's four categories. */
  kind: string;
  points_cost: string;
  /** Centavos. Null on merch, which has no cash face value. */
  cash_value: string | null;
  /** Null means unlimited: a load or a free bet never runs out. */
  stock: string | null;
  active: boolean;
  redeemed: string;
}

interface RedemptionRow {
  id: string;
  ref: string;
  player: string;
  player_ref: string;
  reward: string;
  reward_kind: string;
  points_spent: string;
  /** REQUESTED · APPROVED · REJECTED · FULFILLED · SHIPPED. */
  status: string;
  /** "CODE — note", joined by REDEMPTION_REASON_SEPARATOR. */
  rejection_reason: string | null;
  created_at: string;
  /**
   * When the parcel went out, who sent it, and what they wrote.
   *
   * All three are null unless the status is SHIPPED — guaranteed by
   * `redemption_shipped_is_stamped` in sql/750 rather than left to a handler, so
   * a stamp without the status (or the reverse) is a row the database refuses.
   *
   * `dispatch_note` is the ONLY tracking this platform has: there is no carrier
   * field and no address, so an operator's own sentence is it. Null is common and
   * means nobody wrote one, not that nothing was sent.
   */
  shipped_at: string | null;
  shipped_by: string | null;
  dispatch_note: string | null;
  /** Live sum over points_ledger, so a refusal's re-credit shows on the next
      read of the queue. */
  balance: string;
}

/**
 * An earning rule, as `GET /api/points/earning-rules` returns it.
 *
 * Every integer is a STRING. `points_per_unit` and `unit_amount` are BIGINT and
 * the whole reason this table exists is that the rate used to live in
 * `platform.setting` as the decimal string "0.01" — a value nothing could read
 * without turning it into a float. Sending it as a JSON number here would be
 * the same defect one layer up.
 */
interface EarningRuleRow {
  id: string;
  brand_id: string;
  brand_name: string;
  /** TURNOVER — the sum of stakes. GGR — stakes less wins, floored at zero. */
  basis: string;
  points_per_unit: string;
  /** The money unit the rate is quoted per, IN CENTAVOS. 10000 = ₱100. */
  unit_amount: string;
  count_bonus_funded: boolean;
  active: boolean;
  effective_from: string;
  effective_to: string | null;
  updated_at: string;
  updated_by_name: string | null;
  /** Player-days settled under this rule in the reported window. */
  awarded_player_days: string;
  awarded_points: string;
  /** Centavos the rate was applied to in that window. */
  awarded_turnover: string;
  /** The most recent gaming day this rule settled, as a Manila date. */
  last_gaming_day: string | null;
}

interface EarningRead {
  rules: EarningRuleRow[];
  brandsWithoutRule: { id: string; name: string }[];
  reportedDays: number;
  reportedSince: string;
}

/**
 * What the outstanding points are worth, as a RANGE and never as a figure.
 *
 * Each live cash reward implies its own centavos-per-point, and they disagree:
 * ₱100 for 1,000 points is 10.0, ₱500 for 4,800 is 10.4. Averaging them would
 * put a number on the screen that nobody chose and that moves every time a
 * reward is priced — the same shape as the claim this tile replaces. When the
 * two ends round to the same peso the range collapses to one figure, because
 * "₱278,903 to ₱278,903" reads as a bug rather than as precision.
 */
function liabilityRange(points: bigint, rate: { low: string; high: string }): string {
  const at = (centavosPerPoint: string): bigint =>
    BigInt(Math.round(Number(points) * Number(centavosPerPoint)));
  const low = formatPesos(at(rate.low));
  const high = formatPesos(at(rate.high));
  return low === high ? low : `${low} – ${high}`;
}

interface PointsSummary {
  windowDays: number;
  issued: string;
  redeemed: string;
  outstanding: string;
  playersHolding: string;
  /**
   * Centavos per point, LOW and HIGH, from the live cash rewards — null when
   * nothing prices a point. It is a range because no table holds a rate: each
   * cash reward implies its own, and today they disagree. A single averaged
   * number would be a figure nobody configured, drifting silently every time
   * somebody prices a reward.
   */
  rate: { low: string; high: string; pricedRewards: number } | null;
}

type TabKey = 'redemptions' | 'catalogue' | 'adjustments' | 'earning';

/**
 * The design's chip vocabulary, mapped onto the statuses that exist.
 *
 * SHIPPED IS REAL NOW. It used to be the one chip the design drew that nothing
 * could set, and a dispatched merch item read as Approved for ever. It means "we
 * sent it, on this date, and this admin says so" — and it is the LAST state a
 * merch redemption reaches, because nothing here can observe delivery: no
 * courier integration, no tracking number, no address, no way for a player to
 * confirm receipt. A "Delivered" chip would be a claim somebody ticked.
 *
 * FULFILLED still has no writer. The seed wrote it on one issued reward and
 * nothing else does — it is kept because a row carries it, and the notice below
 * says as much rather than letting it read as a step in a flow.
 */
const STATUS: Record<string, { label: string; tone: 'accent' | 'neutral' | 'outline' | 'quiet' }> = {
  REQUESTED: { label: 'Pending', tone: 'outline' },
  APPROVED: { label: 'Approved', tone: 'accent' },
  SHIPPED: { label: 'Sent', tone: 'neutral' },
  FULFILLED: { label: 'Fulfilled', tone: 'accent' },
  REJECTED: { label: 'Rejected', tone: 'quiet' },
};

const KIND_LABEL: Record<string, string> = { CASH: 'Cash', BONUS: 'Bonus', MERCH: 'Merch' };

/** Decorative. Keyed on the reward KIND, which is real data — never guessed
    from the reward's name, which would be a per-row invention. */
const KIND_ICON: Record<string, string> = {
  CASH: 'device-mobile',
  BONUS: 'ticket',
  MERCH: 'package',
};

/**
 * The capability a verdict needs.
 *
 * A WRITE capability of its own, held by Super Admin and Finance Lead. NOT
 * players.view, which is what the two reads behind this screen use: a view
 * permission must never gate a write, and a redemption verdict spends loyalty
 * currency that carries a cash face value.
 */
const VERDICT_PERMISSION = 'points.verdict' as const;

/**
 * The capability that writes the CATALOGUE, as opposed to deciding a request.
 *
 * Its own key rather than either of the two that already existed, and
 * `packages/db/sql/740_points_reward_writes.sql` carries the argument: reusing
 * `points.verdict` would let whoever approves a redemption invent the reward it
 * was for, and reusing `platform.manage` — Super Admin alone — would have made
 * the whole catalogue Super-Admin-only permanently rather than by accident.
 * Two-factor, because a reward carries a cash face value.
 */
const CATALOGUE_PERMISSION = 'points.manage' as const;

/**
 * PROPOSING a points movement, which is not signing one.
 *
 * Its own key beside `points.verdict`, mirroring `funds.adjust` beside
 * `funds.approve` on the money side: whoever notices a player is owed points is
 * not who should move them alone, and one key for both would make the queue
 * useless to the desk that sees the problem. No second factor, because a
 * proposal moves nothing — the signature is where the factor belongs.
 */
const ADJUST_PERMISSION = 'points.adjust' as const;

/**
 * The capabilities the earning rate sits behind — read and write, and they are
 * deliberately different keys.
 *
 * An earning rule is a platform PARAMETER in exactly the sense `platform.manage`
 * was written for: it silently changes an automated decision about money, it is
 * where this rate literally lived until now (`platform.setting`
 * `points.per_peso_wagered`), and moving it decides how much loyalty currency a
 * night of play mints. NOT `points.verdict`, which means "decide ONE claim on
 * that currency" — setting the price and settling a claim are different acts.
 *
 * The consequence is conservative and worth knowing: `platform.manage` is Super
 * Admin only, so a Finance Lead can READ the rate and cannot move it. Widening
 * that means adding a `points.earning` key to the permission matrix, which is a
 * product decision rather than a screen one.
 */
const RATE_READ_PERMISSION = 'platform.view' as const;
const RATE_WRITE_PERMISSION = 'platform.manage' as const;

/** How a rate reads in a sentence: "1 point per ₱100 wagered". */
function rateSentence(rule: {
  points_per_unit: string;
  unit_amount: string;
  basis: string;
}): string {
  const points = BigInt(rule.points_per_unit);
  const noun = points === 1n ? 'point' : 'points';
  const base = rule.basis === 'GGR' ? 'of margin' : 'wagered';
  return `${formatCount(points)} ${noun} per ${formatPesos(BigInt(rule.unit_amount))} ${base}`;
}

/**
 * Why a redemption was refused — the closed set the route accepts.
 *
 * Prose cannot be grouped, so "how many did we refuse for stock this quarter"
 * needs a code. `points.redemption.rejection_reason` is a bare text column with
 * no CHECK behind it, so the contract in @dayon/contracts is the ONLY thing
 * keeping this closed; that is said out loud under the queue rather than
 * implied.
 */
const REJECTION_REASONS = [
  { code: 'OUT_OF_STOCK', label: 'Out of stock' },
  { code: 'REWARD_WITHDRAWN', label: 'Reward withdrawn' },
  { code: 'INELIGIBLE_PLAYER', label: 'Player not eligible' },
  { code: 'SUSPECTED_ABUSE', label: 'Suspected abuse' },
  { code: 'DUPLICATE_REQUEST', label: 'Duplicate request' },
  { code: 'DELIVERY_UNAVAILABLE', label: 'Cannot be delivered' },
] as const;

const REASON_LABEL: Record<string, string> = Object.fromEntries(
  REJECTION_REASONS.map((r) => [r.code, r.label]),
);

/** The shortest note the route will take. Checked here so the operator is told
    before a round trip; the contract on the server is what actually decides. */
const MIN_NOTE = 10;

/** "18m ago" · "2h ago" · "3d ago" — the Requested column's relative clock. */
function relative(date: Date): string {
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The queue's columns.
 *
 * Built per render rather than held as a module constant because the Action
 * cell now depends on the signed-in role and on the two verdict handlers. The
 * column ORDER is the design's and does not change with the permission: a role
 * that cannot decide still reads the same seven columns and an empty eighth.
 */
/**
 * The earning-rule table's columns.
 *
 * Hoisted out of the component for the reason `buildRedemptionColumns` is: a
 * `render` closure defined during a render is a NEW component type on every
 * pass, so React unmounts and remounts every cell rather than updating it —
 * which loses focus and scroll position on a table an operator is reading.
 */
function buildEarningColumns(
  canEdit: boolean,
  onEdit: (row: EarningRuleRow) => void,
): Column<EarningRuleRow>[] {
  return [
    {
      key: 'brand',
      header: 'Brand',
      render: (r) => (
        <div>
          <div>{r.brand_name}</div>
          <div className="text-subtle" style={{ fontSize: '12px' }}>
            {r.updated_by_name ? `Last changed by ${r.updated_by_name}` : 'Never changed by hand'}
          </div>
        </div>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      render: (r) => <span className="tabular">{rateSentence(r)}</span>,
    },
    {
      key: 'basis',
      header: 'Counts',
      render: (r) => (
        <div>
          <Tag tone="outline">{r.basis === 'GGR' ? 'Stakes less wins' : 'Turnover'}</Tag>
          <div className="text-subtle" style={{ fontSize: '12px' }}>
            {r.count_bonus_funded ? 'Bonus-funded play counts' : 'Bonus-funded play excluded'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.active ? <Tag tone="accent">Live</Tag> : <Tag tone="quiet">Not awarding</Tag>,
    },
    {
      key: 'paid',
      header: 'Awarded',
      numeric: true,
      render: (r) => (
        <div>
          <div className="tabular">{formatCount(BigInt(r.awarded_points))} points</div>
          <div className="text-subtle tabular" style={{ fontSize: '12px' }}>
            {formatCount(BigInt(r.awarded_player_days))} player-days on{' '}
            {formatPesos(BigInt(r.awarded_turnover))}
          </div>
        </div>
      ),
    },
    {
      key: 'last',
      header: 'Last settled',
      render: (r) => (
        <span className="tabular">
          {r.last_gaming_day ?? <span className="text-subtle">never</span>}
        </span>
      ),
    },
    {
      key: 'act',
      header: '',
      numeric: true,
      render: (r) =>
        canEdit ? (
          <Button small onClick={() => onEdit(r)}>
            Change rate
          </Button>
        ) : (
          <span className="text-subtle" style={{ fontSize: '12px' }}>
            Needs {RATE_WRITE_PERMISSION}
          </span>
        ),
    },
  ];
}

function buildRedemptionColumns(
  canDecide: boolean,
  deciding: string | null,
  onApprove: (row: RedemptionRow) => void,
  onReject: (row: RedemptionRow) => void,
  onShip: (row: RedemptionRow) => void,
): Column<RedemptionRow>[] {
  return [
  {
    key: 'ref',
    header: 'Redemption',
    render: (r) => <Tag tone={r.status === 'REQUESTED' ? 'outline' : 'accent'}>{r.ref}</Tag>,
  },
  {
    key: 'player',
    header: 'Player',
    render: (r) => (
      <>
        {r.player}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {r.player_ref}
        </div>
      </>
    ),
  },
  {
    key: 'reward',
    header: 'Reward',
    render: (r) => (
      <>
        {r.reward}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {KIND_LABEL[r.reward_kind] ?? r.reward_kind}
        </div>
      </>
    ),
  },
  {
    key: 'points',
    header: 'Points',
    numeric: true,
    // Points are a whole-number count, not centavos — formatCount, never formatPesos.
    render: (r) => <span className="tabular">{formatCount(BigInt(r.points_spent))}</span>,
  },
  {
    key: 'balance',
    header: 'Points balance',
    secondary: true,
    numeric: true,
    // The player's ledger sum as it stands right now, with the request-time
    // debit already in it. Refusing writes the re-credit, so this figure moves
    // on the re-read that follows a verdict.
    render: (r) => <span className="tabular">{formatCount(BigInt(r.balance))}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => {
      const chip = STATUS[r.status];
      /* Stored as "CODE — note" in one column. Split so the reason reads as
         the rule it is, with the note the operator wrote beneath it. */
      const [code, ...rest] = (r.rejection_reason ?? '').split(REDEMPTION_REASON_SEPARATOR);
      const note = rest.join(REDEMPTION_REASON_SEPARATOR);
      return (
        <>
          <Tag tone={chip?.tone ?? 'neutral'}>{chip?.label ?? r.status.toLowerCase()}</Tag>
          {r.rejection_reason ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {REASON_LABEL[code ?? ''] ?? code}
              {note ? ` · ${note}` : ''}
            </div>
          ) : null}
        </>
      );
    },
  },
  {
    key: 'requested',
    header: 'Requested',
    render: (r) => <span className="text-muted">{relative(new Date(r.created_at))}</span>,
  },
  {
    key: 'sent',
    header: 'Sent',
    secondary: true,
    render: (r) => {
      /* Only merch is ever posted, so the column says "issued" for cash and
         bonus rather than leaving a blank that reads as work outstanding. */
      if (r.reward_kind !== 'MERCH') {
        return <span className="text-subtle">issued, not posted</span>;
      }
      /* FALSY, not `=== null`. The column is null on an unshipped row — but it
         is UNDEFINED on a row served by an API that predates it, which is the
         normal state of a dev server mid-reload, and `undefined === null` is
         false. That fell through to `new Date(undefined)` and rendered
         "NaNd ago" rather than the em dash. */
      if (!r.shipped_at) {
        return r.status === 'APPROVED' ? (
          <span className="text-subtle">waiting to go out</span>
        ) : (
          <span className="text-subtle">—</span>
        );
      }
      return (
        <>
          {relative(new Date(r.shipped_at))}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {r.shipped_by ?? 'an admin since removed'}
            {r.dispatch_note ? ` · ${r.dispatch_note}` : ''}
          </div>
        </>
      );
    },
  },
  {
    key: 'action',
    header: 'Action',
    /* "The Action cell renders Reject + Approve only while status is Pending;
       otherwise it is empty." A decided request has no verdict left to give —
       the routes refuse a second one with a 409 — so drawing the buttons there
       would offer work that cannot be done.

       A role without points.verdict gets no button at all rather than a
       disabled one: a dead control reads as a broken console, whereas the
       capability it is missing is something the operator can act on. */
    render: (r) =>
      /* AN APPROVED MERCH ITEM IS WORK, not a finished row: it is waiting for
         somebody to post it, and this is where they say they have. Cash and
         bonus were issued by the approval itself, so they get no control — and
         the Sent column says which is which rather than leaving a gap. */
      r.status === 'APPROVED' && r.reward_kind === 'MERCH' ? (
        canDecide ? (
          <Button small disabled={deciding !== null} onClick={() => onShip(r)}>
            {deciding === r.id ? 'Recording…' : 'Mark as sent'}
          </Button>
        ) : (
          <span className="text-subtle" style={{ fontSize: '11px' }}>
            needs {VERDICT_PERMISSION}
          </span>
        )
      ) : r.status !== 'REQUESTED' ? (
        <span className="text-subtle">—</span>
      ) : !canDecide ? (
        <span className="text-subtle" style={{ fontSize: '11px' }}>
          needs {VERDICT_PERMISSION}
        </span>
      ) : (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <Button small disabled={deciding !== null} onClick={() => onReject(r)}>
            Reject
          </Button>
          <Button
            small
            variant="primary"
            disabled={deciding !== null}
            onClick={() => onApprove(r)}
          >
            {deciding === r.id ? 'Recording…' : 'Approve'}
          </Button>
        </div>
      ),
  },
  ];
}

export function Points() {
  const { allowed, brandId, brands } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadPlayers = allowed('players.view');
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  // The design keeps the active tab in component state rather than the URL, and
  // the module is hash-routed at #/points with no tab segment. Tabs is a
  // controlled component, so lifting this into a query param later is one line.
  const [tab, setTab] = useState<TabKey>('redemptions');

  /**
   * The three figures this screen used to say it could not source.
   *
   * Its own read, and its own failure flag, for the reason the queue and the
   * catalogue already have separate ones: a dead summary must not empty the
   * tiles beside it, and a summary that failed has no number to show, so its
   * tiles carry an em dash rather than the zero nobody counted.
   */
  const [summary, setSummary] = useState<PointsSummary | null>(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [redemptions, setRedemptions] = useState<RedemptionRow[]>([]);
  const [rewards, setRewards] = useState<RewardRow[]>([]);
  /** The Add reward dialog, and its own reload counter. */
  const [addingReward, setAddingReward] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  /* Its own counter rather than sharing `decisions`. Adding a reward moves the
     catalogue and nothing in the queue; a verdict moves the queue and one
     reward's stock. One trigger would refetch both on every write. */
  const [catalogueWrites, setCatalogueWrites] = useState(0);
  const [queueLoading, setQueueLoading] = useState(true);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  /*
   * Whether a read FAILED, as distinct from succeeding and finding nothing.
   *
   * The load had two states — loading and done — so a rejected read left the
   * arrays empty with loading false, and behind the dismissed error modal the
   * screen reported "0 redemptions read", four tiles at zero and an empty state
   * saying "This is a real zero". None of that had been read. "Nobody has
   * redeemed anything" and "we could not ask" are different facts, and only the
   * first is one an operator can close the queue on.
   *
   * One flag per read, never one for the screen: the queue and the catalogue
   * are separate requests that fail separately, and a shared flag would mark a
   * healthy catalogue broken because the queue returned 503.
   */
  const [queueFailed, setQueueFailed] = useState(false);
  const [catalogueFailed, setCatalogueFailed] = useState(false);

  /* ── Deciding ───────────────────────────────────────────────────────────
     `deciding` holds the id being written, so the row that is in flight says
     so and no second verdict can be started on top of it. `decisions` is
     bumped after every write to re-run BOTH reads — see the effect below. */
  const [deciding, setDeciding] = useState<string | null>(null);
  const [decisions, setDecisions] = useState(0);
  const [rejecting, setRejecting] = useState<RedemptionRow | null>(null);
  const [rejectReason, setRejectReason] = useState<string>(REJECTION_REASONS[0].code);
  const [rejectNote, setRejectNote] = useState('');
  /** Field-level messages, from this screen or from the server's contract. */
  const [rejectFields, setRejectFields] = useState<Record<string, string>>({});

  /**
   * The approved merch item being marked as sent, and the note going with it.
   *
   * A dialog rather than a one-press button, for one reason: the note is the ONLY
   * tracking this platform will ever have — no carrier field, no address — so
   * this is the single moment anybody can record where a parcel went. It is
   * optional, and the button works with it empty, because a dispatch is the
   * expected next step on something already approved rather than a decision that
   * needs justifying.
   */
  const [shipping, setShipping] = useState<RedemptionRow | null>(null);
  const [dispatchNote, setDispatchNote] = useState('');

  /* ── The earning rate ───────────────────────────────────────────────────
     Its own read, its own failure flag and its own reload counter, for the
     reason the queue and the catalogue have theirs: three requests that fail
     separately must not share one flag, or a healthy list is marked broken
     because a different endpoint returned 503. */
  const [earning, setEarning] = useState<EarningRead | null>(null);
  const [earningLoading, setEarningLoading] = useState(true);
  const [earningFailed, setEarningFailed] = useState(false);
  const [rateEdits, setRateEdits] = useState(0);
  const [editingRate, setEditingRate] = useState<EarningRuleRow | null>(null);
  /* Setting a FIRST rate for a brand that has none. The same dialog, because
     the decision is the same decision — only the verb differs. A brand with no
     rule awards nothing at all, so the console has to be able to give it one;
     the migration seeds a rule only for brands that existed when it ran, and a
     brand created afterwards has none until somebody decides on one. */
  const [creatingFor, setCreatingFor] = useState<{ id: string; name: string } | null>(null);
  const [rateBasis, setRateBasis] = useState('TURNOVER');
  const [ratePoints, setRatePoints] = useState('1');
  /** Entered and shown in PESOS; converted to centavos with parsePesos, which
      is exact bigint arithmetic and never a float. */
  const [rateUnit, setRateUnit] = useState('100');
  const [rateBonus, setRateBonus] = useState(false);
  const [rateActive, setRateActive] = useState(true);
  const [rateFields, setRateFields] = useState<Record<string, string>>({});
  /*
   * WHY the rate is changing — PAGCOR item 41, and required only on a change.
   *
   * Setting a brand's first rate brings the rule into existence; moving it changes
   * what every player on that brand earns from tomorrow. The server draws the same
   * line (`changeBody` carries a reason, `createBody` does not), so this box appears
   * for one of the dialog's two jobs and not the other.
   */
  const [rateReason, setRateReason] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPlayers) {
      setQueueLoading(false);
      setCatalogueLoading(false);
      return;
    }
    let cancelled = false;
    setQueueLoading(true);
    setCatalogueLoading(true);

    // Neither request carries brandId, deliberately. No points table has a brand
    // column, so sending the global scope would imply a narrowing the query
    // cannot perform and would quietly return the same rows under a new label.
    // The mismatch is stated on screen instead — see "Not yet available".
    //
    // Two reads, two chains. They shared one Promise.all and one catch, which
    // could only report that SOMETHING had failed; each records its own outcome
    // now, so a dead queue leaves the catalogue reading as the live list it
    // still is, and vice versa.
    api
      .get<{ items: RedemptionRow[] }>('/api/points/redemptions')
      .then((queue) => {
        if (cancelled) return;
        setRedemptions(queue.items);
        setQueueFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared alongside the flag: a queue from the previous read sitting
        // under a failure notice would be read as the current one.
        setRedemptions([]);
        setQueueFailed(true);
        showError({
          title: 'Could not load the Points Mall',
          message: `The redemption queue could not be read. ${
            error instanceof ApiRequestError ? error.message : 'Try again shortly.'
          }`,
        });
      })
      .finally(() => !cancelled && setQueueLoading(false));

    /* A third read, alongside the other two rather than folded into either.
       It aggregates the WHOLE ledger — the queue holds a hundred redemptions
       and the catalogue none of them — so deriving these from either list
       would understate every figure, which is what the old notice correctly
       said and then wrongly concluded was unfixable. */
    api
      .get<PointsSummary>('/api/points/summary')
      .then((read) => {
        if (cancelled) return;
        setSummary(read);
        setSummaryFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        /* Cleared with the flag, and NO error dialog: the tiles say the read
           failed where the number would be, and a modal over a screen whose
           queue and catalogue both loaded would stop an operator working on
           the part that is fine. */
        setSummary(null);
        setSummaryFailed(true);
      });

    api
      .get<{ items: RewardRow[] }>('/api/points/rewards')
      .then((catalogue) => {
        if (cancelled) return;
        setRewards(catalogue.items);
        setCatalogueFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setRewards([]);
        setCatalogueFailed(true);
        showError({
          title: 'Could not load the Points Mall',
          message: `The reward catalogue could not be read. ${
            error instanceof ApiRequestError ? error.message : 'Try again shortly.'
          }`,
        });
      })
      .finally(() => !cancelled && setCatalogueLoading(false));

    return () => {
      cancelled = true;
    };
    /* `decisions` is in the dependency list on purpose: after a verdict both
       reads run again rather than the decided row being patched in place. A
       verdict moves more than its own row — the pending count, the points in
       pending requests, the merch units left and the reward's own "redeemed"
       figure all follow from it, and a patched row would leave every one of
       them stale while looking correct. */
  }, [canReadPlayers, showError, decisions, catalogueWrites]);

  /*
   * THE EARNING RATE, read separately.
   *
   * Its own effect rather than a fourth chain in the one above, because it
   * reloads on a different trigger: a redemption verdict does not move the rate,
   * and moving the rate does not move the queue.
   *
   * Gated in the API on `platform.view`. A role without it is not shown a
   * failure — it is shown the capability it is missing, on the tab itself.
   *
   * And never behind the module's own refusal: the PAGCOR Regulator holds
   * platform.view and not players.view, so it is refused the whole screen, and
   * the rate it would have read is for a tab it never sees.
   */
  useEffect(() => {
    if (!canReadPlayers || !allowed(RATE_READ_PERMISSION)) {
      setEarningLoading(false);
      return;
    }
    let cancelled = false;
    setEarningLoading(true);
    api
      .get<EarningRead>('/api/points/earning-rules')
      .then((read) => {
        if (cancelled) return;
        setEarning(read);
        setEarningFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setEarning(null);
        setEarningFailed(true);
        showError({
          title: 'Could not load the earning rules',
          message: `The rate that awards points could not be read. ${
            error instanceof ApiRequestError ? error.message : 'Try again shortly.'
          }`,
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => !cancelled && setEarningLoading(false));
    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, allowed, showError, rateEdits]);

  /** Open the dialog for a brand that has NO rule, at the platform's own rate. */
  const openNewRate = useCallback((brand: { id: string; name: string }) => {
    setEditingRate(null);
    setCreatingFor(brand);
    setRateBasis('TURNOVER');
    /* The rate the platform has always published — 1 point per ₱100 — as the
       opening suggestion, not as a silent default: the operator still has to
       press the button, and the dialog states what it will do. */
    setRatePoints('1');
    setRateUnit('100.00');
    setRateBonus(false);
    setRateActive(true);
    setRateFields({});
    setRateReason('');
  }, []);

  /** Open the editor with the rule's CURRENT values, never with defaults. */
  const openRate = useCallback((rule: EarningRuleRow) => {
    setCreatingFor(null);
    setEditingRate(rule);
    setRateBasis(rule.basis);
    setRatePoints(rule.points_per_unit);
    // Centavos on the wire, pesos on the form. Both exact.
    setRateUnit(formatPesos(BigInt(rule.unit_amount)).replace(/[₱,]/g, ''));
    setRateBonus(rule.count_bonus_funded);
    setRateActive(rule.active);
    setRateFields({});
    /* Never pre-filled with the last reason given. A reason carried over from the
       previous edit would be attributed to this one, on a row nobody can correct. */
    setRateReason('');
  }, []);

  /**
   * Save the rate.
   *
   * The two numbers are validated HERE only so the operator gets a field-level
   * message instead of a modal for a typo; the API validates them again and the
   * database's own CHECK constraints are what actually hold. Everything that is
   * not a field problem — a permission failure, a second live rule on the brand,
   * a no-op — goes to the ERROR MODAL, never inline: those are outcomes the
   * operator has to acknowledge, not fields to correct.
   */
  const saveRate = useCallback(async () => {
    const target = editingRate ?? creatingFor;
    if (!target) return;

    const fields: Record<string, string> = {};
    if (!/^\d{1,19}$/.test(ratePoints.trim()) || BigInt(ratePoints.trim() || '0') <= 0n) {
      fields['pointsPerUnit'] = 'A whole number of points, greater than zero.';
    }
    let unitCentavos = 0n;
    try {
      unitCentavos = parsePesos(rateUnit);
      if (unitCentavos <= 0n) fields['unitAmount'] = 'The money unit must be more than nothing.';
    } catch {
      fields['unitAmount'] = 'A peso amount, e.g. 100 or 20.50.';
    }
    /* Only on a CHANGE, for the reason the state declaration gives. The floor is
       the database's, imported rather than written here. */
    if (editingRate && rateReason.trim().length < AUDIT_REASON_MIN_LENGTH) {
      fields['reason'] =
        'Say why the rate is changing — this is what the trail will show. At least ' +
        `${AUDIT_REASON_MIN_LENGTH} characters.`;
    }
    if (Object.keys(fields).length > 0) {
      setRateFields(fields);
      return;
    }

    const body = {
      basis: rateBasis,
      pointsPerUnit: ratePoints.trim(),
      unitAmount: unitCentavos.toString(),
      countBonusFunded: rateBonus,
      active: rateActive,
    };
    const brandName = editingRate ? editingRate.brand_name : creatingFor!.name;

    setSavingRate(true);
    try {
      const result = await api.post<{ brandName: string }>(
        editingRate
          ? `/api/points/earning-rules/${editingRate.id}`
          : '/api/points/earning-rules',
        editingRate
          ? { ...body, reason: rateReason.trim() }
          : { ...body, brandId: creatingFor!.id },
      );
      toast(
        `${result.brandName} now earns ${rateSentence({
          points_per_unit: body.pointsPerUnit,
          unit_amount: body.unitAmount,
          basis: body.basis,
        })}. It applies from the next gaming day.`,
      );
      setEditingRate(null);
      setCreatingFor(null);
      setRateEdits((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setRateFields(error.fields);
        return;
      }
      showError({
        title: editingRate
          ? `Could not change ${brandName}’s earning rate`
          : `Could not set ${brandName}’s earning rate`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The rate was not changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSavingRate(false);
    }
  }, [
    editingRate,
    creatingFor,
    rateBasis,
    ratePoints,
    rateUnit,
    rateBonus,
    rateActive,
    rateReason,
    showError,
    toast,
  ]);

  /**
   * Whether this role may decide at all.
   *
   * The same capability the two routes ask for. `players.view` — the gate on
   * the reads below — is deliberately not enough: it is a VIEW permission, and
   * a view permission must never gate a write.
   */
  const canDecide = allowed(VERDICT_PERMISSION);

  /**
   * Whether this role may write the CATALOGUE — a different key from the verdict.
   *
   * `points.manage` defines what is offered and what it is worth;
   * `points.verdict` decides one player's request. Folding them together would
   * let whoever approves a redemption also invent the reward it was for, and the
   * separation between setting the terms and deciding a case is one this platform
   * keeps everywhere else.
   */
  const canManageCatalogue = allowed(CATALOGUE_PERMISSION);

  /** The reward being taken off the shelf or put back, and the form beside it. */
  const [offering, setOffering] = useState<RewardRow | null>(null);
  const [offerReason, setOfferReason] = useState('COMMERCIAL_DECISION');
  const [offerNote, setOfferNote] = useState('');
  const [offerNoteError, setOfferNoteError] = useState<string>();
  const [offerBusy, setOfferBusy] = useState(false);

  const applyOffered = async () => {
    if (!offering) return;
    const note = offerNote.trim();
    if (note.length < 10) {
      setOfferNoteError('Say why — at least 10 characters.');
      return;
    }
    setOfferNoteError(undefined);
    setOfferBusy(true);
    try {
      const result = await api.post<{ name: string; offered: boolean; rewardsLive: string }>(
        `/api/points/rewards/${offering.id}/offered`,
        { offered: !offering.active, reasonCode: offerReason, note },
      );
      toast(
        result.offered
          ? `${result.name} is on the shelf — ${result.rewardsLive} rewards live.`
          : `${result.name} withdrawn — ${result.rewardsLive} rewards left live. ` +
              'Redemptions already raised against it still need a verdict.',
      );
      setOffering(null);
      setCatalogueWrites((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: 'The reward was not moved',
        message:
          error instanceof ApiRequestError ? error.message : 'Nothing was changed on this reward.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setOfferBusy(false);
    }
  };
  const canAdjust = allowed(ADJUST_PERMISSION);

  /**
   * Approve or refuse, as the checker.
   *
   * Approving takes the merch unit and moves no points; refusing returns
   * exactly what the request took. Every refusal the API can give is surfaced
   * as itself — a maker-checker conflict reads differently from "out of stock",
   * and both read differently from a permission failure — so the modal carries
   * the server's own sentence rather than a generic one.
   */
  const decide = useCallback(
    async (row: RedemptionRow, verdict: 'approve' | 'reject', body: unknown) => {
      setDeciding(row.id);
      try {
        const result = await api.post<{ pointsReturned?: string; stockLeft?: string | null }>(
          `/api/points/redemptions/${row.id}/${verdict}`,
          body,
        );
        toast(
          verdict === 'approve'
            ? `${row.ref} approved — ${row.reward} for ${row.player}.${
                result.stockLeft === null || result.stockLeft === undefined
                  ? ''
                  : ` ${formatCount(BigInt(result.stockLeft))} left in stock.`
              }`
            : `${row.ref} refused — ${formatCount(
                BigInt(result.pointsReturned ?? row.points_spent),
              )} points returned to ${row.player}.`,
        );
        setRejecting(null);
        setRejectNote('');
        setRejectFields({});
        // Re-read rather than patch the row: a verdict moves the tiles and the
        // catalogue's stock as well as this one row.
        setDecisions((n) => n + 1);
      } catch (error: unknown) {
        // Field-level problems stay inline in the dialog; everything else is a
        // decision that did NOT happen, and that goes to the modal.
        if (verdict === 'reject' && error instanceof ApiRequestError && error.fields) {
          setRejectFields(error.fields);
          return;
        }
        showError({
          title:
            verdict === 'approve' ? `Could not approve ${row.ref}` : `Could not refuse ${row.ref}`,
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The verdict was not recorded. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setDeciding(null);
      }
    },
    [showError, toast],
  );

  const openReject = useCallback((row: RedemptionRow) => {
    setRejecting(row);
    setRejectReason(REJECTION_REASONS[0].code);
    setRejectNote('');
    setRejectFields({});
  }, []);

  const approve = useCallback(
    (row: RedemptionRow) => {
      void decide(row, 'approve', {});
    },
    [decide],
  );

  /**
   * Record that an approved merch item went out.
   *
   * On `points.verdict`, the same key as the two verdicts and NOT the catalogue
   * key: deciding whether the platform owes a player a thing and then posting it
   * are two halves of one job, and the person who priced the reward should not be
   * the one confirming it left the building.
   */
  const ship = useCallback(async () => {
    const row = shipping;
    if (!row) return;
    setDeciding(row.id);
    try {
      const trimmed = dispatchNote.trim();
      await api.post<{ ref: string; deliveryTracked: boolean }>(
        `/api/points/redemptions/${row.id}/ship`,
        trimmed ? { dispatchNote: trimmed } : {},
      );
      /* The toast says what this does NOT mean. An operator told "delivered"
         stops chasing a parcel nothing is tracking. */
      toast(
        `${row.ref} marked as sent — ${row.reward} to ${row.player}. Nothing tracks it from here, so the player asking where it is cannot be answered from this screen.`,
      );
      setShipping(null);
      setDispatchNote('');
      // Re-read: the status moves, and the queue orders by it.
      setDecisions((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: `Could not mark ${row.ref} as sent`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was recorded. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setDeciding(null);
    }
  }, [shipping, dispatchNote, showError, toast]);

  const openShip = useCallback((row: RedemptionRow) => {
    setShipping(row);
    setDispatchNote('');
  }, []);

  const redemptionColumns = useMemo(
    () => buildRedemptionColumns(canDecide, deciding, approve, openReject, openShip),
    [canDecide, deciding, approve, openReject, openShip],
  );

  const canSetRate = allowed(RATE_WRITE_PERMISSION);
  const earningColumns = useMemo(
    () => buildEarningColumns(canSetRate, openRate),
    [canSetRate, openRate],
  );

  /** The header line and the tile band cover both reads, so they wait for both. */
  const loading = queueLoading || catalogueLoading;

  /** Every headline figure below is computed from rows actually on this page. */
  const figures = useMemo(() => {
    const pending = redemptions.filter((r) => r.status === 'REQUESTED');

    // Summed from redemption.points_spent — what these requests cost the
    // players who made them. The debit already happened, at request; refusing
    // one here returns exactly this much of it.
    const pointsPending = pending.reduce((sum, r) => sum + BigInt(r.points_spent), 0n);

    const byKind = new Map<string, number>();
    for (const r of pending) byKind.set(r.reward_kind, (byKind.get(r.reward_kind) ?? 0) + 1);

    const stocked = rewards.filter((r) => r.stock !== null);
    const unitsLeft = stocked.reduce((sum, r) => sum + Number(r.stock ?? '0'), 0);

    return {
      pending,
      pointsPending,
      // "1 merch · 1 cash", the breakdown the design puts under the tile.
      kindMix: [...byKind]
        .map(([kind, n]) => `${n} ${(KIND_LABEL[kind] ?? kind).toLowerCase()}`)
        .join(' · '),
      live: rewards.filter((r) => r.active).length,
      stocked: stocked.length,
      unitsLeft,
    };
  }, [redemptions, rewards]);

  // A badge is a count, and a count is a claim about what was read. A failed
  // read has no number to show, so its badge is omitted rather than drawn as 0.
  const tabs: { key: TabKey; label: string; count?: number }[] = [
    {
      key: 'redemptions',
      label: 'Redemptions',
      ...(queueFailed ? {} : { count: figures.pending.length }),
    },
    { key: 'catalogue', label: 'Catalogue', ...(catalogueFailed ? {} : { count: rewards.length }) },
    /* NO COUNT HERE, deliberately. The queue is read by the tab's own component
       when it opens, so a badge on this bar would either force a second read on
       every visit to the module or show a number from the last time somebody
       looked. The tab's caption carries the figure that was actually read. */
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'earning', label: 'Earning rules' },
  ];

  // Both endpoints sit behind players.view, so a role without it sees the
  // capability it is missing rather than an error dialog it cannot act on.
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Points Mall">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  const scopedBrand = brands.find((b) => b.id === brandId);

  /* Each half of the header line describes its own read. A read that failed has
     no figure to report, so it says so rather than reporting the zero it never
     counted — and the half that succeeded keeps its real numbers. */
  const queueSummary = queueFailed
    ? 'the redemption queue could not be read'
    : `${formatCount(redemptions.length)} redemptions read · ${figures.pending.length} awaiting a decision`;
  const catalogueSummary = catalogueFailed
    ? 'the reward catalogue could not be read'
    : `${formatCount(rewards.length)} rewards in the catalogue`;

  return (
    <>
      <ScreenHeader
        /* The sidebar's own name for the module, kept letter for letter. An
           operator who clicked "Points Mall" has to land on something that says
           "Points Mall" back, and the capability refusal above is titled the
           same way for the same reason — one screen, one name, whichever path
           reaches it. */
        title="Points Mall"
        purpose="Where the loyalty desk decides the reward requests players have already paid points for, and sets how points are earned and what they buy."
        /*
          The caption kept exactly as it was: both halves, all three states.

          It is tri-state and the three states are NOT interchangeable.
          "Loading…" means neither read has come back yet; "could not be read"
          means that half FAILED, so there is no figure to report and none is
          invented; and a count means the platform genuinely answered. Each half
          describes its own read because the two fail apart — the catalogue can
          answer while the queue is dark — and collapsing the failed state into a
          zero would turn "we could not ask" into "there is nothing waiting for a
          verdict", which on this screen is somebody's reward request sitting
          unanswered while the header says there is none. It is the same
          distinction the tiles, the tab badges and the table's own empty state
          each go out of their way to keep.
        */
        meta={loading ? 'Loading…' : `${queueSummary} · ${catalogueSummary}`}
        actions={
          <>
            {/* Both header actions work now. Neither is hidden from a role that
                cannot use it — the control is rendered and says which capability
                it needs, because a control that vanishes teaches an operator the
                screen is broken while one that explains itself teaches them who to
                ask. */}
            {canAdjust ? (
              <Button onClick={() => setAdjusting(true)}>Points adjustment</Button>
            ) : (
              <>
                <Button disabled aria-describedby="adjust-gap">
                  Points adjustment
                </Button>
                <span id="adjust-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                  Raising one is for a Super Admin; signing one
                  needs somebody else who can sign it off, and it cannot be the same
                  person.
                </span>
              </>
            )}
            {canManageCatalogue ? (
              <Button variant="primary" onClick={() => setAddingReward(true)}>
                <Icon name="plus" />
                Add reward
              </Button>
            ) : (
              <>
                <Button variant="primary" disabled aria-describedby="reward-gap">
                  <Icon name="plus" />
                  Add reward
                </Button>
                <span id="reward-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                  Adding a reward is not something your role can do — only a Super Admin can,
                  and a second factor is required, because a reward carries a cash value players
                  redeem points against.
                </span>
              </>
            )}
          </>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          <strong>The lifecycle, as it now runs.</strong> A request in this queue has already cost{' '}
          {/* The ledger reasons are REDEMPTION and POINTS_REVERSAL_REASON, drawn the way the
              player's Points tab draws them. */}
          its points: a negative &ldquo;Redemption&rdquo; entry sits in{' '}
          the points ledger naming the redemption.{' '}
          <strong>Approving</strong> takes one unit off a merch reward&rsquo;s stock and moves no
          points, because taking them again would charge twice for one reward.{' '}
          <strong>Refusing</strong> returns exactly what was debited, as a{' '}
          &ldquo;{sentence(POINTS_REVERSAL_REASON)}&rdquo; entry against the same
          redemption — and it first checks that a debit is actually there, because a refusal that
          credits a charge nobody made would mint points rather than return them. Each verdict is
          saved together with its audit entry, and the maker of a request may not decide it.
          <br />
          Nothing in this console raises a redemption, and nothing should: a player spends their
          own points from the player app, and the points leave their balance in the same moment
          the request is made. That is what makes a refusal safe — the reversal has a charge to
          return, rather than crediting one nobody made.
        </div>
      </div>

      {/* ── Headline figures. Four tiles, all four computed from rows on this
             page. The design's own four — points issued 30d, points redeemed,
             outstanding liability, pending redemptions — are not these: only
             the last one has a source, and the rest are named as gaps below. ── */}
      {loading && redemptions.length === 0 && rewards.length === 0 ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-grid">
          {/* An em dash where a read failed, never a zero: "0 pending" is a
              figure an operator closes the queue on. Each tile follows the read
              it is counted from, so the catalogue tiles keep their real numbers
              while the queue is dark. */}
          <Stat
            label="Pending redemptions"
            value={queueFailed ? '—' : formatCount(figures.pending.length)}
            delta={
              queueFailed
                ? 'the redemption queue could not be read'
                : figures.kindMix || 'nothing awaiting a verdict'
            }
          />
          <Stat
            label="Points in pending requests"
            value={queueFailed ? '—' : formatCount(figures.pointsPending)}
            delta={
              queueFailed
                ? 'no request was read, so nothing is summed here'
                : 'already taken from these players at request · refusing one returns it'
            }
          />
          {/* ── The three the design asked for, and this screen carried as gaps
                 for longer than they were gaps. Each is one aggregate over
                 points.points_ledger; the rollup the old notice said they
                 needed does not exist and is not needed. ── */}
          <Stat
            label="Points issued 30d"
            value={summaryFailed || !summary ? '—' : formatCount(BigInt(summary.issued))}
            delta={
              summaryFailed
                ? 'the ledger summary could not be read'
                : 'earned and awarded · Asia/Manila gaming days'
            }
          />
          <Stat
            label="Points redeemed 30d"
            value={summaryFailed || !summary ? '—' : formatCount(BigInt(summary.redeemed))}
            delta={
              summaryFailed
                ? 'the ledger summary could not be read'
                : summary && BigInt(summary.redeemed) > 0n && BigInt(summary.issued) > 0n
                  ? `${(Number(summary.issued) / Number(summary.redeemed)).toFixed(0)} issued for every 1 spent`
                  : 'spent against the catalogue'
            }
          />
          <Stat
            label="Outstanding liability"
            value={
              summaryFailed || !summary
                ? '—'
                : summary.rate
                  ? liabilityRange(BigInt(summary.outstanding), summary.rate)
                  : formatCount(BigInt(summary.outstanding))
            }
            delta={
              summaryFailed
                ? 'the ledger summary could not be read'
                : !summary
                  ? ''
                  : summary.rate
                    ? `${formatCount(BigInt(summary.outstanding))} points held by ${formatCount(
                        BigInt(summary.playersHolding),
                      )} players · priced from ${summary.rate.pricedRewards} live cash ${
                        summary.rate.pricedRewards === 1 ? 'reward' : 'rewards'
                      }, not a configured rate`
                    : `points held by ${formatCount(
                        BigInt(summary.playersHolding),
                      )} players · no live cash reward prices a point, so this cannot be shown in pesos`
            }
          />
          <Stat
            label="Rewards live"
            value={catalogueFailed ? '—' : formatCount(figures.live)}
            delta={
              catalogueFailed
                ? 'the reward catalogue could not be read'
                : `of ${formatCount(rewards.length)} in the catalogue`
            }
          />
          <Stat
            label="Merch units left"
            value={catalogueFailed ? '—' : formatCount(figures.unitsLeft)}
            delta={
              catalogueFailed
                ? 'no catalogue was read, so no stock is counted'
                : `${formatCount(figures.stocked)} stocked rewards · decrements at approval`
            }
          />
        </div>
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} label="Points Mall sections" />

      {tab === 'redemptions' && (
        <Panel>
          <div className="notice notice-quiet" style={{ marginBottom: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              Approve and Reject both write for real, behind{' '}
              <span className="signin-perm">{VERDICT_PERMISSION}</span>. A verdict is given once —
              a second one is refused with the state the request is already in — and the person who
              raised a request may not decide it. Approving a merch reward with no stock left is
              refused rather than shipped: refuse it with the reason{' '}
              <strong>Out of stock</strong> instead, and the player gets their points back.
              <br />
              {/*
                THE IDENTIFIERS THIS PARAGRAPH USED TO PRINT, kept here for
                whoever maintains it rather than shown to the person deciding a
                reward request.

                The stored reason is `points.redemption.rejection_reason`, a
                bare text column with no CHECK behind it — the same fact the
                comment on REJECTION_REASONS above records — so the closed set
                lives in @dayon/contracts and is enforced by the verdict route
                alone. Anything writing to the database another way could still
                put prose there.

                There is no `approved_at` on the row either: the only record of
                WHEN a verdict was given is the audit row written in the same
                transaction as the verdict.

                Both of those matter to an operator. Neither NAME does: a
                column name is not something they can look up, ask anybody for,
                or repeat to support, so the names stay in here and the
                consequences go on the screen.
              */}
              The reason codes are a closed set, and the only thing holding that set closed is the
              check this platform makes when you send a refusal — nothing underneath it. The reason
              itself is kept as ordinary text, so — unlike a withdrawal — a refusal written any
              other way than through this screen could carry no code at all. Nothing is stored for
              the moment a verdict was given, either: when a request was decided, and by whom, is
              in the audit trail rather than on the request itself.
            </div>
          </div>

          {queueLoading && redemptions.length === 0 ? (
            <div className="skeleton" style={{ height: '240px' }}>
              &nbsp;
            </div>
          ) : (
            <DataTable
              caption="Points redemptions in queue order — pending first, then newest"
              columns={redemptionColumns}
              rows={redemptions}
              rowKey={(r) => r.id}
              empty={
                queueFailed ? (
                  <EmptyState icon="gift" title="The redemption queue could not be read">
                    The redemptions could not be fetched,
                    so this table is empty because nothing came back — not because nothing has been
                    redeemed. There may be requests waiting for a verdict; this screen cannot say.
                    Try again shortly.
                  </EmptyState>
                ) : (
                  /*
                    "READ FRESH, NOT FROM A CACHE" IS A CLAIM ABOUT `points.redemption`.

                    This empty state is allowed to call itself a real zero only
                    because the queue is a live read of that table every time
                    the tab opens — there is no store, no snapshot and no
                    rollup in front of it, so nothing can be stale in a way that
                    would make an empty table mean "nobody has looked lately"
                    instead of "nobody has redeemed".

                    If a cache is ever put in front of that read, this sentence
                    becomes a lie and NOTHING WILL FAIL. Change it in the same
                    commit. The operator-facing half deliberately does not name
                    the table: what they can act on is that the emptiness is
                    current and real, which is the distinction the failed-read
                    state above exists to be told apart from.
                  */
                  <EmptyState icon="gift" title="Nothing has been redeemed">
                    No player has spent points against the catalogue yet. This is a real zero: the
                    queue was read fresh when this tab opened, not taken from a cache, and it came
                    back genuinely empty rather than unanswered.
                  </EmptyState>
                )
              }
            />
          )}

          {redemptions.length === 100 && (
            <div className="pagination">
              Showing the 100 most recent redemptions. The list stops at 100 and cannot be paged,
              so older rows are not reachable from this screen.
            </div>
          )}
        </Panel>
      )}

      {tab === 'catalogue' && (
        <>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* reward.kind is CASH, BONUS or MERCH; the design filed rewards under Load, Bonus,
                  Merch and Raffle. */}
              A reward records one of three kinds: Cash, Bonus and Merch. There is no Load or
              Raffle kind, so the chips below show the stored kind — a raffle entry reads as Merch
              and a GCash load reads as Cash. Renaming them on screen would invent categories the
              platform cannot filter on.
            </div>
          </div>

          {catalogueLoading && rewards.length === 0 ? (
            <div className="skeleton" style={{ height: '200px' }}>
              &nbsp;
            </div>
          ) : catalogueFailed ? (
            <EmptyState icon="storefront" title="The catalogue could not be read">
              The rewards could not be fetched, so
              nothing is listed here. That is not an empty catalogue — no reward was read at all,
              and the rewards a player can still see are unknown to this screen. Try again shortly.
            </EmptyState>
          ) : rewards.length === 0 ? (
            <EmptyState icon="storefront" title="The catalogue is empty">
              {/* "Adding one needs a write route that does not exist yet" went stale when Add
                  reward (POST /api/points/rewards) landed. */}
              No rewards have been published, so nothing can be redeemed.{' '}
              {canManageCatalogue
                ? 'Add reward, above, puts one in the catalogue.'
                : `Adding one needs ${CATALOGUE_PERMISSION}, which your role does not hold.`}
            </EmptyState>
          ) : (
            /* The design's card grid, auto-fit at a 270px minimum. Cards are not
               clickable here — there is no reward detail route to open. */
            <ul
              aria-label="Reward catalogue"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
                gap: 'var(--space-4)',
                listStyle: 'none',
                margin: 0,
                padding: 0,
              }}
            >
              {rewards.map((r) => (
                <li className="card elev-sm" key={r.id}>
                  <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                    <span
                      style={{
                        width: '38px',
                        height: '38px',
                        flex: 'none',
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-tag-accent-bg)',
                        color: 'var(--color-tag-accent-fg)',
                        fontSize: '19px',
                      }}
                    >
                      <Icon name={KIND_ICON[r.kind] ?? 'gift'} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="card-title" style={{ fontSize: '15px' }}>
                        {r.name}
                      </div>
                      <div className="text-subtle tabular" style={{ fontSize: '12px' }}>
                        {/* "{points} pts · {stock}" — null stock is genuinely
                            unlimited, not an unknown, so it is safe to say so. */}
                        {formatCount(BigInt(r.points_cost))} pts ·{' '}
                        {r.stock === null
                          ? 'Unlimited'
                          : `${formatCount(BigInt(r.stock))} left`}
                      </div>
                    </div>
                    <Tag className="header-spacer" tone="neutral">
                      {KIND_LABEL[r.kind] ?? r.kind}
                    </Tag>
                  </div>
                  <div className="card-meta">
                    <span>
                      {/* cash_value is centavos, so BigInt before formatting. */}
                      {r.cash_value === null
                        ? 'no cash face value'
                        : `${formatPesos(BigInt(r.cash_value))} face value`}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="tabular">
                      {formatCount(BigInt(r.redeemed))} redeemed
                    </span>
                    {!r.active && (
                      <Tag className="header-spacer" tone="quiet">
                        unpublished
                      </Tag>
                    )}
                  </div>
                  {/* ── On the shelf, or off it ──────────────────────────────
                      A reward is NEVER deleted — redemptions point at it — so
                      this flag is the only way one stops being offered. It was
                      the write this screen did not have: the Add reward form
                      could hold a reward back and then admit, in its own words,
                      that turning it on later meant going to the database. */}
                  {canManageCatalogue ? (
                    <div className="row-actions header-spacer">
                      <Button
                        small
                        variant={r.active ? 'secondary' : 'primary'}
                        onClick={() => {
                          setOfferNote('');
                          setOfferNoteError(undefined);
                          setOffering(r);
                        }}
                      >
                        {r.active ? 'Withdraw' : 'Offer it'}
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {tab === 'adjustments' && (
        <AdjustmentQueue writes={catalogueWrites} onDone={() => setCatalogueWrites((n) => n + 1)} />
      )}

      {tab === 'earning' &&
        (!allowed(RATE_READ_PERMISSION) ? (
          <EmptyState icon="lock-key" title="Earning rules">
            Reading the rate that awards points needs the {RATE_READ_PERMISSION} capability. Your
            role does not hold it. It is a read: nothing on this tab changes anything without{' '}
            {RATE_WRITE_PERMISSION} as well.
          </EmptyState>
        ) : earningLoading ? (
          <EmptyState icon="sliders" title="Reading the earning rules…">
            One rule per brand, and what each has actually paid.
          </EmptyState>
        ) : earningFailed || !earning ? (
          <EmptyState icon="warning" title="The earning rules could not be read">
            This is a failed request, not an empty list — nothing here has been counted. Reload the
            module to try again.
          </EmptyState>
        ) : (
          <>
            {/* ── What the rate IS, per brand ─────────────────────────────
                   A table rather than the design's Bronze-to-VIP-4 ladder: the
                   ladder's per-tier multiplier and 500k monthly cap still have
                   no table, no counter and no endpoint, and drawing them here
                   would be a hardcoded 1.0×–2.5× an operator would read as live
                   configuration. What IS real is one rate per brand, and it is
                   now real all the way down to the ledger. ── */}
            <Panel
              title="Earning rules"
              actions={
                <span className="text-subtle" style={{ fontSize: '12.5px' }}>
                  {formatCount(earning.rules.length)} rule
                  {earning.rules.length === 1 ? '' : 's'} · figures cover the last{' '}
                  {earning.reportedDays} gaming days
                </span>
              }
            >
              {earning.rules.length === 0 ? (
                <EmptyState icon="sliders" title="No brand in your scope has an earning rate">
                  Nothing is awarding points. This is a real zero: the read succeeded and found no
                  rule.
                </EmptyState>
              ) : (
                <DataTable<EarningRuleRow>
                  caption="Loyalty earning rules, one per brand, with what each has awarded"
                  rows={earning.rules}
                  rowKey={(r) => r.id}
                  columns={earningColumns}
                />
              )}
            </Panel>

            {/* A brand with no rule awards nothing at all, which is the most
                important thing this tab can say and is said by an ABSENCE
                otherwise. Named, so it cannot be missed. */}
            {earning.brandsWithoutRule.length > 0 && (
              <Panel title="Brands awarding nothing">
                <div className="stack">
                  <div className="notice notice-quiet">
                    <Icon name="warning" />
                    <div>
                      {earning.brandsWithoutRule.length === 1 ? 'This brand has' : 'These brands have'}{' '}
                      no live earning rule, so no play on{' '}
                      {earning.brandsWithoutRule.length === 1 ? 'it' : 'them'} mints a single point.
                      The gaming days stay OPEN rather than being settled at zero — set a rate and
                      the next nightly run settles every day still inside its three-day window.
                    </div>
                  </div>
                  {earning.brandsWithoutRule.map((b) => (
                    <div key={b.id} className="row">
                      <strong>{b.name}</strong>
                      <div className="header-spacer">
                        {allowed(RATE_WRITE_PERMISSION) ? (
                          <Button small onClick={() => openNewRate(b)}>
                            Set a rate
                          </Button>
                        ) : (
                          <span className="text-subtle" style={{ fontSize: '12px' }}>
                            Needs {RATE_WRITE_PERMISSION}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* ── How the award actually behaves ───────────────────────────
                   Every sentence here is a property of `points.award` and of
                   sql/460, not a description of intent. An operator changing a
                   rate has to know when it bites and what it does to the
                   fraction of a point nobody can pay. ── */}
            <Panel title="How points are awarded">
              <div className="stack">
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>Once per player per gaming day, at 00:40 Manila.</strong> The nightly
                    job settles the gaming days that have CLOSED — midnight to midnight in Manila,
                    not UTC — and writes one &ldquo;Turnover&rdquo; entry per player per day,
                    labelled with that day&rsquo;s Manila date. A day still being
                    played is never settled, because settling it would claim the day on a fraction
                    of its turnover.
                  </div>
                </div>
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>Rounded once, half up, on the whole day.</strong> The day&rsquo;s
                    eligible amount is multiplied by the rate in integers and rounded to the nearest
                    whole point, with the exact half going to the player. It is NOT rounded per
                    round: truncating each spin separately would pay nothing at all on a ₱15 stake
                    at 1 point per ₱100, keeping a fraction of every row in the house&rsquo;s
                    favour. The residual is at most half a point per player per day and it points
                    both ways.
                  </div>
                </div>
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>Voided rounds and self-excluded players earn nothing.</strong> A voided
                    round is never counted. A player under a self-exclusion — in force now, or in
                    force while those rounds were played — has the day recorded as settled at zero,
                    so it can never be paid later if the exclusion lapses.
                  </div>
                </div>
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>A change here applies from the next gaming day.</strong> A rule pays a
                    day only if it was already in force when that day began, so no day is ever half
                    at one rate and half at another. Days already settled are not restated: every
                    award records the rate it was computed under.
                  </div>
                </div>
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>Running the job twice does not pay twice.</strong> Each award is claimed
                    once per player per gaming day, and the credit is posted only for the days
                    that claim succeeded on, in the same write. Nothing else stands behind a points
                    balance, so that one rule is what stops a day being claimed twice.
                  </div>
                </div>
              </div>
            </Panel>
          </>
        ))}

      {/* ── Everything the design shows that this console cannot source. Stated
             once, in full, so no figure above has to be taken on trust. ── */}
      {/*
        WHAT THIS SCREEN CANNOT DO — and only that.
        
        The panel's purpose is right and is kept: no figure above should have to
        be taken on trust. What it had grown into was a changelog written for
        whoever built it — "Add reward now works", "Brand scope arrived with
        it", "The earlier note here said this could not be built because
        points_ledger has no approver columns", "tidying an enum by editing
        history" — alongside points.reward, points.redemption, a CHECK
        constraint and a capability key, on a screen an operator opens to run a
        rewards catalogue.
        
        An operator needs what is TRUE, not what changed. Everything below is a
        limit they can hit today; every sentence about how it got that way is
        gone, and lives in git where somebody can look it up.
      */}
      <Panel title="What this screen cannot do">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Nobody has set what a point is worth.</strong> The first two tiles are
              counted straight from the points ledger. The peso figure on{' '}
              <strong>Outstanding liability</strong> is the only number here worked out from
              something nobody agreed: each live cash reward implies its own rate — ₱100 for 1,000
              points is 10 centavos a point, ₱500 for 4,800 is 10.4 — so the tile shows the range
              those rewards imply, and it moves whenever one is repriced. Read it as what players
              could claim today, not as a figure agreed with finance. A single number would look
              like one.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>A reward cannot be edited.</strong> One priced wrongly is withdrawn and
              replaced, never corrected in place — a redemption already raised against it was
              raised at the old price, and quietly changing it would rewrite what a player is
              owed. Withdrawing deletes nothing: the reward stops being offered and stays in the
              catalogue, because the records that reference it still need it to exist.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Nothing here knows whether a parcel arrived.</strong> Marking a merch item
              as sent is the last state it reaches. There is no delivered: no courier is connected,
              no tracking number or address is stored anywhere, and a player has no way to confirm
              receipt. A screen claiming a parcel arrived because somebody was asked to tick a box
              is worse than one admitting it does not know. If a player asks where their item is,
              this screen cannot answer — the dispatch note is the only thing anybody recorded.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Stock is protected by the platform, not by the table.</strong> Approving a
              redemption takes a unit and is refused at zero, so a request can never take stock
              that is not there. Anything writing to the catalogue directly could, which is why
              stock is only ever changed by approving or withdrawing.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>The brand filter at the top does not narrow these figures.</strong>{' '}
              {scopedBrand
                ? `It is set to ${scopedBrand.name}, and the tiles above still count every brand.`
                : 'The tiles above count every brand, whatever it is set to.'}{' '}
              Deciding a redemption does respect it: one belonging to a player outside your brands
              is refused as though it were not there. So you can see more of this queue than you
              can decide on — and a reward offered to one brand cannot be redeemed by a player on
              another, even if they name it directly.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Fulfilled is a word you may see and nothing writes.</strong> One old
              redemption carries it. It is left alone rather than rewritten, because correcting
              history to tidy a label is the worse trade.
            </div>
          </div>
        </div>
      </Panel>

      {/* ── Changing the earning rate ──────────────────────────────────────
             Two integers and three switches. The rate is entered as "how many
             points" per "how much money", never as a decimal like the 0.01 this
             replaces: a decimal is a float the moment anything reads it, and
             this one mints a currency that buys ₱100 GCash loads. ── */}
      <Dialog
        open={editingRate !== null || creatingFor !== null}
        onClose={() => {
          setEditingRate(null);
          setCreatingFor(null);
        }}
        title={
          editingRate
            ? `${editingRate.brand_name} — earning rate`
            : creatingFor
              ? `${creatingFor.name} — set an earning rate`
              : 'Earning rate'
        }
        actions={
          <>
            <Button
              onClick={() => {
                setEditingRate(null);
                setCreatingFor(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" disabled={savingRate} onClick={() => void saveRate()}>
              {savingRate ? 'Saving…' : editingRate ? 'Change the rate' : 'Set the rate'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {creatingFor ? (
                <>
                  <strong>{creatingFor.name} awards nothing today.</strong> This gives it a rate.
                  It applies from the <strong>next gaming day</strong> — and the nightly job will
                  also settle any earlier gaming day still inside its three-day window, at this
                  rate.
                </>
              ) : (
                <>
                  This applies from the <strong>next gaming day</strong> — a rule pays a day only if
                  it was already in force when that day began, so no day is ever half at one rate
                  and half at another. Days already settled keep the rate they were computed under
                  and are not restated.
                </>
              )}
            </div>
          </div>

          <Field
            label="What earns"
            hint="Turnover is the sum of stakes. Margin is stakes less wins, floored at zero for the day — a player who finished ahead produced none, and this platform issues no negative points."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={rateBasis}
                onChange={(e) => setRateBasis(e.target.value)}
              >
                <option value="TURNOVER">Turnover — every peso staked</option>
                <option value="GGR">Margin — stakes less wins</option>
              </Select>
            )}
          </Field>

          <Field
            label="Points awarded"
            hint="A whole number. With the money unit below this reads as “N points per ₱X”."
            {...(rateFields['pointsPerUnit'] ? { error: rateFields['pointsPerUnit'] } : {})}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="numeric"
                value={ratePoints}
                onChange={(e) => setRatePoints(e.target.value)}
                placeholder="1"
              />
            )}
          </Field>

          <Field
            label="Per this much money"
            hint="In pesos. Stored in centavos as a whole number, so the rate is a ratio of two integers and never a decimal."
            {...(rateFields['unitAmount'] ? { error: rateFields['unitAmount'] } : {})}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="decimal"
                value={rateUnit}
                onChange={(e) => setRateUnit(e.target.value)}
                placeholder="100.00"
              />
            )}
          </Field>

          <Choice
            checked={rateBonus}
            onChange={(e) => setRateBonus(e.target.checked)}
            label="Bonus-funded play earns points too"
          />
          <div className="text-subtle" style={{ fontSize: '12px' }}>
            Off by default. A bonus-funded round is staked with the operator&rsquo;s own money;
            paying loyalty currency on it means the house funds the stake and the reward.
          </div>

          <Choice
            checked={rateActive}
            onChange={(e) => setRateActive(e.target.checked)}
            label="This rate is live"
          />
          <div className="text-subtle" style={{ fontSize: '12px' }}>
            Turning it off stops the nightly job awarding anything on this brand. Days played while
            it is off stay OPEN rather than settled at zero, so turning it back on settles every day
            still inside the job&rsquo;s three-day window.
          </div>

          {/* ── Why the rate is changing ───────────────────────────────────
                 On a CHANGE only. Setting a brand's first rate creates the rule and
                 has no prior state to justify; moving it changes what every player
                 on that brand earns from tomorrow, and the audit row records the
                 old and new figures but nothing about the decision. The server
                 draws the same line, so an operator never meets a box the route
                 would have ignored. */}
          {editingRate && (
            <Field
              label="Why is the rate changing?"
              hint="Goes on the audit record. The figures are already there — this is the part that is not recoverable from them."
              {...(rateFields['reason'] ? { error: rateFields['reason'] } : {})}
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  value={rateReason}
                  onChange={(e) => setRateReason(e.target.value)}
                  placeholder="Loyalty review of 1 October — margin basis moved to turnover."
                />
              )}
            </Field>
          )}
        </div>
      </Dialog>

      {/* ── Refusing a redemption ──────────────────────────────────────────
             The only verdict that needs input: a code, because a refusal report
             groups on it, and a note, because a code on its own reads as a form
             number six months later.

             NOT DISMISSIBLE, AND DANGER RATHER THAN QUIET. Two deliberate
             choices, and the first is the easy one to get backwards. A stray
             Escape here throws away a decision somebody came to this screen to
             make — a reason chosen, a note typed — so the dialog does not close
             on Escape or on a click outside it at any point, and `Cancel` is
             the only way out. Guarding only the moment the write is in flight
             would protect the one moment the decision has already been made.

             The tone is `notice-danger` because this verdict is both of the
             things that tone is reserved for: it moves a figure, returning
             points to a balance that rewards are priced against, and it cannot
             be taken back — only a REQUESTED redemption is decidable, so the
             row this closes can never be reopened from here. ── */}
      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        dismissible={false}
        title={rejecting ? `Refuse ${rejecting.ref}?` : 'Refuse redemption'}
        actions={
          <>
            <Button onClick={() => setRejecting(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={deciding !== null}
              onClick={() => {
                if (!rejecting) return;
                const note = rejectNote.trim();
                if (note.length < MIN_NOTE) {
                  setRejectFields({
                    note: `At least ${MIN_NOTE} characters. The player is told this.`,
                  });
                  return;
                }
                void decide(rejecting, 'reject', { reasonCode: rejectReason, note });
              }}
            >
              {deciding ? 'Recording…' : 'Refuse and return the points'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              {rejecting ? (
                <>
                  This returns{' '}
                  <strong>{formatCount(BigInt(rejecting.points_spent))} points</strong> to{' '}
                  {rejecting.player}, as a &ldquo;{sentence(POINTS_REVERSAL_REASON)}&rdquo; entry
                  naming{' '}
                  <span className="tabular">{rejecting.ref}</span> — exactly what the request took,
                  not a recomputation from the reward&rsquo;s current price. No merch stock moves,
                  because none was taken.
                  <br />
                  <strong>A verdict is final.</strong> Nothing here reopens a refused request —
                  the way back is {rejecting.player} redeeming again from the player app, with
                  the points they have just been given back. No message goes out: they see the
                  return in their own points history, named as a refused reward, and the reason
                  and note below stay in this console for the refusal report and the audit trail.
                </>
              ) : (
                'Refusing returns exactly the points the request took.'
              )}
            </div>
          </div>

          <Field
            label="Reason"
            hint="A closed set, so a report can count refusals by cause. It is kept together with your note, the reason first."
            {...(rejectFields['reasonCode'] ? { error: rejectFields['reasonCode'] } : {})}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              >
                {REJECTION_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint={`At least ${MIN_NOTE} characters. A code on its own reads as a form number; the note is what tells the player something they can act on.`}
            {...(rejectFields['note'] ? { error: rejectFields['note'] } : {})}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="The speaker is discontinued and will not be restocked."
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* Marking a merch item as sent. The note is optional and is the only
          tracking this platform has, which is why it is asked for once here
          rather than never. */}
      <Dialog
        open={shipping !== null}
        onClose={() => setShipping(null)}
        title={shipping ? `Mark ${shipping.ref} as sent?` : 'Mark as sent'}
        actions={
          <>
            <Button onClick={() => setShipping(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={deciding !== null}
              disabled={deciding !== null}
              onClick={() => void ship()}
            >
              Mark as sent
            </Button>
          </>
        }
      >
        {shipping && (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="package" />
              <div>
                <strong>
                  {shipping.reward} to {shipping.player}
                </strong>{' '}
                ({shipping.player_ref}). The points were spent when they asked and the unit came off
                stock when it was approved, so nothing moves now — this records that the parcel left.
                <br />
                <strong>This is the last state it reaches.</strong> Nothing here tracks a delivery:
                no courier is integrated and no address is stored, so once this is recorded the
                platform cannot say where the item is. If the player asks later, the note below is
                the only thing anybody will have.
              </div>
            </div>

            <Field
              label="Dispatch note (optional)"
              hint="The only tracking this platform has. A courier and a receipt number here are worth more than anything else on this screen six weeks from now."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={dispatchNote}
                  onChange={(e) => setDispatchNote(e.target.value)}
                  placeholder="Handed to LBC, receipt 4471"
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>

      {/* RE-READ, never patch: a new reward changes the catalogue tab's count,
          the chips above it and the rate comparison an operator makes against
          the rest of the list. */}
      <PointsAdjustmentDialog
        open={adjusting}
        onClose={() => setAdjusting(false)}
        onDone={() => setCatalogueWrites((n) => n + 1)}
      />

      {/* ── Withdraw a reward, or put it back ───────────────────────────────
             One dialog for both directions, and the two do not deserve the same
             tone. Withdrawing takes a priced reward off every player's shelf,
             which is why it is `notice-warn`; putting one back is the ordinary
             "how this works" accent. Neither is `notice-danger`, because
             neither is irreversible and neither moves money: the flag is the
             only thing that changes, the reward itself is never deleted, and
             this same control is the undo. Reaching for danger here would spend
             the one tone that has to still mean something on the refusal
             dialogs above.

             Escape and a click outside are both off, for the reason the
             refusal dialog gives: the note is a mandatory ten characters and
             somebody has typed it, and a keypress aimed at an autocomplete list
             should not throw the note and the decision away together. ── */}
      <Dialog
        open={offering !== null}
        onClose={() => setOffering(null)}
        dismissible={false}
        title={
          offering === null
            ? ''
            : offering.active
              ? `Withdraw ${offering.name}?`
              : `Offer ${offering.name}?`
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setOffering(null)} disabled={offerBusy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void applyOffered()} disabled={offerBusy}>
              {offering?.active ? 'Withdraw it' : 'Offer it'}
            </Button>
          </>
        }
      >
        {offering === null ? null : (
          <div className="stack">
            <div className={offering.active ? 'notice notice-warn' : 'notice'}>
              <Icon name={offering.active ? 'warning' : 'info'} />
              <div>
                {offering.active ? (
                  <>
                    Players stop seeing it from now on. <strong>Nothing already in flight
                    changes:</strong> the points were spent when each player asked, every
                    redemption already raised still needs a verdict, and an approved merch item
                    still has to go in the post. A reward is never deleted — redemptions point at
                    it, so removing one would leave records of transactions nobody can explain.
                    <br />
                    The way back is this same control on the row: offering it again puts it on
                    the shelf at the price it still carries. If it is the last reward offered,
                    players open the catalogue to an empty list until something goes back on.
                  </>
                ) : (
                  <>
                    Players can see it and redeem against it from now on, at{' '}
                    <strong>{formatCount(BigInt(offering.points_cost))} points</strong>
                    {offering.cash_value === null
                      ? ''
                      : ` for ${formatPesos(BigInt(offering.cash_value))}`}
                    . Check the price is what you meant before you press — the catalogue is what a
                    player is owed.
                  </>
                )}
              </div>
            </div>
            <Field label="Reason">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={offerReason}
                  onChange={(e) => setOfferReason(e.target.value)}
                >
                  <option value="COMMERCIAL_DECISION">Commercial decision</option>
                  <option value="PRICED_WRONG">Priced wrong</option>
                  <option value="OUT_OF_STOCK">Out of stock</option>
                  <option value="SEASONAL_OFFER">Seasonal offer</option>
                  <option value="NEW_CAMPAIGN">New campaign</option>
                  <option value="PARTNER_PROMOTION">Partner promotion</option>
                  <option value="REPLACING_WITHDRAWN_REWARD">Replacing a withdrawn reward</option>
                </Select>
              )}
            </Field>
            <Field
              label="Note"
              error={offerNoteError}
              hint="At least 10 characters. The catalogue log keeps it with a full snapshot of the reward as it stood."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={offerNote}
                  onChange={(e) => setOfferNote(e.target.value)}
                  placeholder="Cash value was set a decimal place out; replacing it with a corrected one."
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>

      <AddRewardDialog
        open={addingReward}
        onClose={() => setAddingReward(false)}
        onDone={() => setCatalogueWrites((n) => n + 1)}
      />
    </>
  );
}
